import type { MouseEvent } from 'react';
import { useMemo } from 'react';

import { Button } from '#/components/Button/Button';
import { Divider } from '#/components/Divider/Divider';
import { AnimatedCheckmark } from '#/components/Icon/AnimatedCheckmark';
import { Icon } from '#/components/Icon/Icon';
import { Pill } from '#/components/Pill/Pill';
import { Stack } from '#/components/Stack/Stack';
import { H1 } from '#/components/Text/H1';
import { PostSearchInputContainer } from '#/containers/PostSearchInputContainer';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiAuthor } from '#/modules/cms/api/types/CmsApiAuthor';
import type { CmsApiCategory } from '#/modules/cms/api/types/CmsApiCategory';
import type { CmsApiSubsite } from '#/modules/cms/api/types/CmsApiSubsite';
import type { CmsApiTag } from '#/modules/cms/api/types/CmsApiTag';
import type {
  CmsApiAuthorSlug,
  CmsApiCategorySlug,
  CmsApiTagSlug,
} from '#/modules/cms/api/types/CmsApiTypedId';
import { PostSearchCategoryFilter } from '#/modules/postSearch/components/CategoryFilter/PostSearchCategoryFilter';
import { PostSearchIncludedSubsites } from '#/modules/postSearch/components/IncludedSubsites/PostSearchIncludedSubsites';
import { PostSearchLoadingFilters } from '#/modules/postSearch/components/Loading/PostSearchLoadingFilters';
import { spacing } from '#/theme/theme';

import {
  PostSearchSidebarSearchBlurb,
  PostSearchSidebarSearchLinksHolder,
  SubscribeButtonContents,
} from './PostSearchSidebar.styled';
import { PostSearchSidebarParentSubsiteHeader } from './PostSearchSidebarParentSubsiteHeader';
import { PostSearchSidebarSearchLink } from './PostSearchSidebarSearchLink';

type Props = {
  homePageUrl: string;
  subsite: CmsApiSubsite;
  parentSubsite: CmsApiSubsite | undefined;
  isSubscribed: boolean;
  onSubscribe: (event: MouseEvent<HTMLButtonElement>) => void;
  author: CmsApiAuthor | undefined;
  authorSlug: CmsApiAuthorSlug | undefined;
  onRemoveAuthor: () => void;
  tag: CmsApiTag | undefined;
  tagSlug: CmsApiTagSlug | undefined;
  onRemoveTag: () => void;
  categories: CmsApiCategory[] | undefined;
  selectedCategorySlugs: CmsApiCategorySlug[];
  onSelectParentCategory: (categorySlug: CmsApiCategorySlug) => void;
  onDeselectParentCategory: () => void;
  toggleSubCategory: (categorySlug: CmsApiCategorySlug[]) => void;
};

export function PostSearchSidebar({
  homePageUrl,
  subsite,
  parentSubsite,
  isSubscribed,
  onSubscribe,
  author,
  authorSlug,
  onRemoveAuthor,
  tag,
  tagSlug,
  onRemoveTag,
  categories,
  selectedCategorySlugs,
  onSelectParentCategory,
  onDeselectParentCategory,
  toggleSubCategory,
}: Props) {
  const { i18n } = useI18n();

  const categoriesLoaded =
    subsite.includedSubsites.length > 0 || Boolean(categories);

  const categoryNameBySlug = useMemo(() => {
    const record: Record<string, string> = {};

    categories?.forEach((category) => {
      record[category.slug] = category.name;
    });

    return record;
  }, [categories]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header
        data-qa-id="search-results-title"
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        {parentSubsite?.homePage && (
          <PostSearchSidebarParentSubsiteHeader
            homePage={parentSubsite.homePage}
            publicName={parentSubsite.publicName}
          />
        )}

        <H1 m={0}>{subsite.publicName}</H1>

        {subsite.searchBlurb && (
          <PostSearchSidebarSearchBlurb>
            {subsite.searchBlurb}
          </PostSearchSidebarSearchBlurb>
        )}
      </header>

      <Divider marginTop={0} marginBottom={8} />

      <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Stack direction="row" spacing={spacing[4]}>
          {authorSlug && (
            <Pill
              key={authorSlug}
              suffix={
                <Button
                  type="button"
                  onClick={onRemoveAuthor}
                  aria-label={i18n(`Clear %(option)s filter`, {
                    option: author?.name ?? authorSlug,
                  })}
                  variant="tertiary-grey"
                  noPadding
                  icon={{ name: 'close', size: 14 }}
                />
              }
            >
              {author?.name ?? authorSlug}
            </Pill>
          )}

          {tagSlug && (
            <Pill
              key={tagSlug}
              suffix={
                <Button
                  type="button"
                  onClick={onRemoveTag}
                  aria-label={i18n(`Clear %(option)s filter`, {
                    option: tag?.tag ?? tagSlug,
                  })}
                  variant="tertiary-grey"
                  noPadding
                  icon={{ name: 'close', size: 14 }}
                />
              }
            >
              {tag?.tag ?? tagSlug}
            </Pill>
          )}

          {selectedCategorySlugs.map((categorySlug) => {
            const categoryName = categoryNameBySlug[categorySlug];

            return (
              <Pill
                key={categorySlug}
                suffix={
                  <Button
                    type="button"
                    onClick={onDeselectParentCategory}
                    aria-label={i18n(`Clear %(option)s filter`, {
                      option: categoryName || categorySlug,
                    })}
                    variant="tertiary-grey"
                    noPadding
                    icon={{ name: 'close', size: 14 }}
                  />
                }
              >
                {categoryName || categorySlug}
              </Pill>
            );
          })}
        </Stack>

        <PostSearchInputContainer
          homePageUrl={homePageUrl}
          searchPlaceholderText={subsite.searchPlaceholderText}
          usageContext="searchResults"
        />
      </section>

      {(subsite.includedSubsites.length > 0 ||
        (categories && categories.length > 0)) && (
        <section data-qa-id="search-filters">
          <Divider marginTop={8} marginBottom={16} />

          {subsite.includedSubsites.length > 0 ? (
            <PostSearchIncludedSubsites
              parentSubsite={subsite}
              subsites={subsite.includedSubsites}
            />
          ) : (
            <PostSearchCategoryFilter
              allCategories={categories}
              selectedCategorySlugs={selectedCategorySlugs}
              label={subsite.categoryLabel}
              onSelectParentCategory={onSelectParentCategory}
              onDeselectParentCategory={onDeselectParentCategory}
              toggleSubCategory={toggleSubCategory}
            />
          )}
        </section>
      )}

      {!categoriesLoaded && <PostSearchLoadingFilters />}

      {categoriesLoaded && subsite.canSubscribe && (
        <section style={{ marginLeft: -8 }}>
          <Button
            type="button"
            variant="tertiary-grey"
            onClick={onSubscribe}
            qaId="subscribe-to-subsite"
            size="medium"
            disabled={isSubscribed}
          >
            <SubscribeButtonContents>
              {isSubscribed && (
                <AnimatedCheckmark colorName="brandGreen" size={16} />
              )}

              <div>
                {isSubscribed
                  ? i18n('Subscribed!')
                  : subsite.subscribeText ||
                    i18n('Subscribe to Our Newsletter')}
              </div>
              {!isSubscribed && <Icon size={16} name="arrow-right" />}
            </SubscribeButtonContents>
          </Button>
        </section>
      )}

      {categoriesLoaded && subsite.searchLinks.length > 0 && (
        <PostSearchSidebarSearchLinksHolder>
          {subsite.searchLinks.map((searchLink, index) => (
            <PostSearchSidebarSearchLink
              // oxlint-disable-next-line react/no-array-index-key
              key={index}
              searchLink={searchLink}
            />
          ))}
        </PostSearchSidebarSearchLinksHolder>
      )}
    </div>
  );
}
